import React from 'react';
import { motion } from 'framer-motion';
import { Crown, Target, CheckCircle, XCircle } from 'lucide-react';
export function LeadershipResponsibilityPage() {
  return <div className="min-h-screen bg-surface-bg" data-id="element-974" data-mp="3gfctkzdpaorr">
      {/* Hero Section */}
      <section className="pt-32 pb-20 lg:pt-40 lg:pb-28 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto" data-id="element-975" data-mp="3vbkddyl75o6i">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="text-center" data-id="element-976" data-mp="3q9uxa4j4akbk">
          <div className="inline-flex items-center gap-2 mb-6" data-id="element-977" data-mp="hu1jvyk31oyq">
            <span className="w-8 h-px bg-teal" data-id="element-978" data-mp="cvq8hje536ee"></span>
            <span className="text-xs font-semibold text-teal uppercase tracking-[0.2em]" data-id="element-979" data-mp="cx1ku7lnhmh4">
              Capability
            </span>
            <span className="w-8 h-px bg-teal" data-id="element-980" data-mp="mupib5an582z"></span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-ink-dark leading-tight mb-8" data-id="element-981" data-mp="mscsk5yn5tfd">
            Leadership Responsibility
          </h1>

          <p className="text-xl text-ink-muted leading-relaxed max-w-3xl mx-auto" data-id="element-982" data-mp="31hsai44o9a9e">
            At SyNeural, leadership responsibility refers to the ability to
            carry power, influence, and consequence in a way that protects
            larger systems rather than destabilizing them. It involves making
            decisions with awareness of broader effects, accepting
            accountability, managing complexity, and acting with a sense of duty
            toward the institution, community, or system being led.
          </p>
        </motion.div>
      </section>

      {/* Introduction */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-983" data-mp="3u56a69zr8jx7">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-984" data-mp="3067tz9c20q4">
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-985" data-mp="39hojbridh8ah">
            Leadership responsibility is not simply about authority, confidence,
            or visibility. It is about whether someone can handle larger
            consequence without collapsing into ego, impulsivity, factionalism,
            or short-term thinking. As responsibility increases, so does the
            cost of poor judgment. That is why leadership responsibility sits at
            the top of the SyNeural capability model.
          </p>
        </motion.div>
      </section>

      {/* Why It Matters */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-surface-muted border-y border-line" data-id="element-986" data-mp="5zn2wtyuap8p">
        <div className="max-w-4xl mx-auto" data-id="element-987" data-mp="2cpkzgu1zu8hl">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-988" data-mp="2cqmjvp4wg7bz">
            <div className="flex items-center gap-4 mb-6" data-id="element-989" data-mp="djxr92rhfukr">
              <div className="w-12 h-12 rounded-lg bg-navy flex items-center justify-center text-white" data-id="element-990" data-mp="dhbjb20ojd20">
                <Target size={24} data-id="element-991" data-mp="ig61q2uqizll" />
              </div>
              <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-992" data-mp="4hofyaoe3x6b">
                Why It Matters
              </h2>
            </div>
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-993" data-mp="8i01dpoku1iu">
              Leadership judgment scales. A poor decision at the top can affect
              teams, institutions, communities, or entire systems. Weak
              leadership responsibility can produce instability, mistrust,
              internal damage, and preventable crises. Strong leadership
              responsibility supports continuity, resilience, long-term
              planning, and healthier institutions. It is one of the clearest
              distinctions between influence and readiness.
            </p>
          </motion.div>
        </div>
      </section>

      {/* What Strong Leadership Responsibility Looks Like */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-994" data-mp="3c11em51vmcyc">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-995" data-mp="3c0ro8gcx63s4">
          <div className="flex items-center gap-4 mb-6" data-id="element-996" data-mp="2xa2b38fweelm">
            <div className="w-12 h-12 rounded-lg bg-teal flex items-center justify-center text-white" data-id="element-997" data-mp="2tbkzocpg2hye">
              <CheckCircle size={24} data-id="element-998" data-mp="2oa5f0lr6idgj" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-999" data-mp="2jcdr02tnnbhm">
              What Strong Leadership Responsibility Looks Like
            </h2>
          </div>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-1000" data-mp="yu31ztlhar80">
            People strong in leadership responsibility are able to absorb
            pressure, think beyond personal image, handle dissent, weigh
            consequences, and act with regard for the larger system rather than
            immediate ego needs. They understand that leadership is not merely
            about being followed. It is about stewarding complexity, maintaining
            trust, and carrying responsibility without destabilizing the
            structure around them.
          </p>
        </motion.div>
      </section>

      {/* What Weak Leadership Responsibility Looks Like */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white border-y border-line" data-id="element-1001" data-mp="tw1nllyzzg2v">
        <div className="max-w-4xl mx-auto" data-id="element-1002" data-mp="3tjjbq9enmu1b">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-1003" data-mp="3tgneyoejmbeg">
            <div className="flex items-center gap-4 mb-6" data-id="element-1004" data-mp="1eg67c9h1nziv">
              <div className="w-12 h-12 rounded-lg bg-navy-light flex items-center justify-center text-white" data-id="element-1005" data-mp="1ehr1vz3czigq">
                <XCircle size={24} data-id="element-1006" data-mp="lqz7n75ut3w1" />
              </div>
              <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1007" data-mp="lrs8jri4sz69">
                What Weak Leadership Responsibility Looks Like
              </h2>
            </div>
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-1008" data-mp="urxavn8gx7dk">
              Weak leadership responsibility often appears as impulsive
              decision-making, image management, blame shifting, inability to
              absorb criticism, factional behavior, and failure to recognize how
              decisions affect others downstream. In institutions and governance
              settings, this can produce instability, poor succession,
              fragmented trust, and repeated strategic error.
            </p>
          </motion.div>
        </div>
      </section>

      {/* How It Can Be Strengthened */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-1009" data-mp="1zrviz1amns7s">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1010" data-mp="24ovggntevmna">
          <div className="flex items-center gap-4 mb-6" data-id="element-1011" data-mp="2ioeozhq4x004">
            <div className="w-12 h-12 rounded-lg bg-green-soft flex items-center justify-center text-white" data-id="element-1012" data-mp="2noj699yv9yre">
              <Crown size={24} data-id="element-1013" data-mp="3bedfzinw377f" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1014" data-mp="3bckaw7sxcv1j">
              How It Can Be Strengthened
            </h2>
          </div>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-1015" data-mp="1g2c7pqke1r18">
            Leadership responsibility can be strengthened through systems
            thinking, accountability structures, crisis simulations, mentorship,
            consequence review, role-based training, and experience in carrying
            progressively larger responsibility with reflection and support. In
            the SyNeural framework, it is treated as a developmental capability
            that becomes increasingly important as influence, consequence, and
            public trust expand.
          </p>
        </motion.div>
      </section>
    </div>;
}